<template>
  <div class="audit-container">
    <div class="p-d-flex p-jc-end p-mb-2">
      <Button
        icon="pi pi-refresh"
        label="Atualizar"
        class="p-button-outlined p-button-sm"
        @click="clearFiltersAndReload"
      />
    </div>
    <DataTable
      :value="audits"
      :filters.sync="filters"
      :loading="loading"
      responsive-layout="scroll"
      filter-display="row"
      class="p-datatable-sm"
      scrollable
      scroll-height="800px"
      :expandedRows.sync="expandedRows"
      @row-expand="onRowExpand"
      @row-collapse="onRowCollapse"
      :lazy="true"
      @filter="onFilterOrSort"
      @sort="onFilterOrSort"
    >
      <template #empty>
        Nenhuma auditoria.
      </template>

      <Column :expander="true" />

      <Column field="user_name" header="Utilizador">
        <template #filter="{ filterModel, filterCallback }">
          <DebouncedFilterInput
            v-model="filterModel.value"
            :callback="filterCallback"
            placeholder="Filtrar por utilizador"
          />
        </template>
        <template #body="slotProps">
          {{ slotProps.data.user_name || 'Sistema' }}
        </template>
      </Column>

      <Column field="event" header="Evento">
        <template #filter="{ filterModel, filterCallback }">
          <Dropdown
            v-model="filterModel.value"
            :options="eventOptions"
            option-label="label"
            option-value="value"
            placeholder="Todos"
            :show-clear="true"
            class="p-column-filter"
            @change="filterCallback()"
          />
        </template>
        <template #body="slotProps">
          <span :class="'event-badge event-' + slotProps.data.event">
            {{ getEventLabel(slotProps.data.event) }}
          </span>
        </template>
      </Column>

      <Column field="auditable_type" header="Modelo">
        <template #filter="{ filterModel, filterCallback }">
          <Dropdown
            v-model="filterModel.value"
            :options="modelOptions"
            option-label="label"
            option-value="value"
            placeholder="Todos"
            :show-clear="true"
            class="p-column-filter"
            @change="filterCallback()"
          />
        </template>
        <template #body="slotProps">
          {{ getModelLabel(slotProps.data.auditable_type_short || slotProps.data.auditable_type) }}
        </template>
      </Column>

      <Column field="auditable_id" header="ID Registo">
        <template #filter="{ filterModel, filterCallback }">
          <DebouncedFilterInput
            v-model="filterModel.value"
            :callback="filterCallback"
            placeholder="Filtrar por ID"
          />
        </template>
      </Column>

      <Column field="created_at" header="Data">
        <template #body="slotProps">
          {{ formatDate(slotProps.data.created_at) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <v-date-picker
            mode="date"
            v-model="filterModel.value"
            @input="onDateFilterChange($event, filterModel, filterCallback)"
            :masks="{
              input: 'DD-MM-YYYY',
              data: 'DD-MM-YYYY'
            }"
          >
            <template v-slot="{ inputValue, inputEvents }">
              <input
                class="p-column-filter p-inputtext p-component"
                :value="inputValue"
                v-on="inputEvents"
              />
            </template>
          </v-date-picker>
        </template>
      </Column>

      <template #expansion="slotProps">
        <div class="expanded-row-content" v-if="hasValues(slotProps.data)">
          <div class="audit-values-grid">
            <div class="audit-values-col" v-if="slotProps.data.old_values">
              <h6 class="p-mb-2">Valores Anteriores</h6>
              <div class="values-container">
                <div v-for="(value, key) in parseValues(slotProps.data.old_values)" :key="'old-' + key" class="value-row">
                  <span class="value-key">{{ key }}:</span>
                  <span class="value-val old-value">{{ value }}</span>
                </div>
              </div>
            </div>
            <div class="audit-values-col" v-if="slotProps.data.new_values">
              <h6 class="p-mb-2">Valores Novos</h6>
              <div class="values-container">
                <div v-for="(value, key) in parseValues(slotProps.data.new_values)" :key="'new-' + key" class="value-row">
                  <span class="value-key">{{ key }}:</span>
                  <span class="value-val new-value">{{ value }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="p-mt-3 audit-meta">
            <span v-if="slotProps.data.ip_address">
              <i class="pi pi-globe"></i> {{ slotProps.data.ip_address }}
            </span>
            <span v-if="slotProps.data.url" class="p-ml-3">
              <i class="pi pi-link"></i> {{ slotProps.data.url }}
            </span>
          </div>
        </div>

        <div v-else class="p-text-center p-p-4">
          <i class="pi pi-info-circle" style="font-size: 2rem; color: #6c757d;"></i>
          <p class="p-mt-2">Sem detalhes de alteração disponíveis.</p>
        </div>
      </template>
    </DataTable>

    <div class="p-d-flex p-jc-between p-ai-center p-mt-3 audit-paginator">
      <div class="p-d-flex p-ai-center">
        <span class="p-mr-2">Por página:</span>
        <Dropdown
          v-model="perPage"
          :options="[10, 20, 50, 100]"
          @change="onPerPageChange"
          class="p-column-filter"
        />
      </div>
      <div class="p-d-flex p-ai-center">
        <span class="p-mr-3">
          Página {{ currentPage }}<template v-if="rangeFrom && rangeTo"> ({{ rangeFrom }}–{{ rangeTo }})</template>
        </span>
        <Button
          icon="pi pi-angle-left"
          label="Anterior"
          class="p-button-outlined p-button-sm p-mr-2"
          :disabled="!hasPrev || loading"
          @click="goToPreviousPage"
        />
        <Button
          icon="pi pi-angle-right"
          icon-pos="right"
          label="Seguinte"
          class="p-button-outlined p-button-sm"
          :disabled="!hasNext || loading"
          @click="goToNextPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import auditsService from '@/services/audits.service';
import DebouncedFilterInput from "@/components/elements/DebouncedFilterInput.vue";
import { FilterMatchMode } from "primevue/api";

export default {
  name: 'PatAudits',
  components: { DebouncedFilterInput },
  props: {
    patId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      audits: [],
      expandedRows: [],
      currentPage: 1,
      perPage: 20,
      hasNext: false,
      hasPrev: false,
      rangeFrom: null,
      rangeTo: null,
      eventOptions: [
        { label: 'Criado', value: 'created' },
        { label: 'Atualizado', value: 'updated' },
        { label: 'Eliminado', value: 'deleted' },
        { label: 'Restaurado', value: 'restored' },
      ],
      modelOptions: [
        { label: 'PAT', value: 'App\\Models\\Pat' },
        { label: 'Intervenção', value: 'App\\Models\\Task' },
        { label: 'Dossier Interno', value: 'App\\Models\\InternalDossier' },
        { label: 'Linhas Dossier', value: 'App\\Models\\InternalDossierLines' },
        { label: 'Recolha', value: 'App\\Models\\PatPickup' },
        { label: 'Receção', value: 'App\\Models\\PatReception' },
        { label: 'Envio', value: 'App\\Models\\PatDispatch' },
        { label: 'Equipamento', value: 'App\\Models\\Equipments' },
        { label: 'Ficheiro', value: 'App\\Models\\Attachments' },
      ],
      filters: {
        user_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        event: { value: null, matchMode: FilterMatchMode.EQUALS },
        auditable_type: { value: null, matchMode: FilterMatchMode.EQUALS },
        auditable_id: { value: null, matchMode: FilterMatchMode.CONTAINS },
        created_at: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
    };
  },
  watch: {
    patId() {
      this.currentPage = 1;
      this.fetchAudits();
    }
  },
  mounted() {
    this.fetchAudits();
  },
  methods: {
    clearFiltersAndReload() {
      this.filters = {
        user_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        event: { value: null, matchMode: FilterMatchMode.EQUALS },
        auditable_type: { value: null, matchMode: FilterMatchMode.EQUALS },
        auditable_id: { value: null, matchMode: FilterMatchMode.CONTAINS },
        created_at: { value: null, matchMode: FilterMatchMode.CONTAINS },
      };
      this.currentPage = 1;
      this.fetchAudits();
    },
    onFilterOrSort() {
      this.currentPage = 1;
      this.fetchAudits();
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.fetchAudits();
    },
    goToPreviousPage() {
      if (this.hasPrev && this.currentPage > 1) {
        this.currentPage -= 1;
        this.fetchAudits();
      }
    },
    goToNextPage() {
      if (this.hasNext) {
        this.currentPage += 1;
        this.fetchAudits();
      }
    },
    onRowExpand(event) {
      this.expandedRows = [event.data];
    },
    onRowCollapse(event) {
      this.expandedRows = this.expandedRows.filter(row => row.id !== event.data.id);
    },
    onDateFilterChange(date, filterModel, filterCallback) {
      if (date) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        filterModel.value = `${year}-${month}-${day}`;
      } else {
        filterModel.value = null;
      }

      filterCallback();
    },
    buildQueryParams(page = 1) {
      const params = new URLSearchParams();
      params.append('page', page);

      for (const [key, filter] of Object.entries(this.filters)) {
        if (filter.value !== null && filter.value !== '') {
          const backendKey = key === 'user_name' ? 'username' : key;
          params.append(`filters[${backendKey}]`, filter.value);
        }
      }

      return params;
    },
    async fetchAudits() {
      try {
        this.loading = true;

        const params = this.buildQueryParams(this.currentPage);
        params.append('per_page', this.perPage);

        const response = await auditsService.getByPat(this.patId, params);

        if (response) {
          this.audits = response.data || [];
          this.currentPage = response.current_page || this.currentPage;
          this.rangeFrom = response.from || null;
          this.rangeTo = response.to || null;
          this.hasNext = !!response.next_page_url;
          this.hasPrev = !!response.prev_page_url;
        }
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível carregar as auditorias.',
          life: 3000
        });
      } finally {
        this.loading = false;
      }
    },
    getEventLabel(event) {
      const map = {
        'created': 'Criado',
        'updated': 'Atualizado',
        'deleted': 'Eliminado',
        'restored': 'Restaurado',
      };
      return map[event] || event;
    },
    getModelLabel(modelName) {
      const map = {
        'Pat': 'PAT',
        'Task': 'Intervenção',
        'InternalDossier': 'Dossier Interno',
        'InternalDossierLines': 'Linhas Dossier',
        'PatPickup': 'Recolha',
        'PatReception': 'Receção',
        'PatDispatch': 'Envio',
        'Equipments': 'Equipamento',
        'Attachments': 'Ficheiro',
      };
      return map[modelName] || modelName;
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('pt-PT');
    },
    parseValues(values) {
      if (!values) return {};
      if (typeof values === 'string') {
        try {
          return JSON.parse(values);
        } catch {
          return {};
        }
      }
      return values;
    },
    hasValues(data) {
      const oldValues = this.parseValues(data.old_values);
      const newValues = this.parseValues(data.new_values);
      return (oldValues && Object.keys(oldValues).length > 0) ||
             (newValues && Object.keys(newValues).length > 0);
    },
  }
}
</script>

<style scoped>
.audit-container {
  padding: 1rem;
}

.expanded-row-content {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  margin: 0.5rem 0;
  padding: 1rem;
  width: 100%;
}

.audit-values-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
}

.audit-values-col {
  flex: 1 1 0;
  min-width: 300px;
}

.values-container {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 0.75rem;
}

.value-row {
  padding: 0.25rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.value-row:last-child {
  border-bottom: none;
}

.value-key {
  font-weight: 600;
  color: #495057;
  margin-right: 0.5rem;
}

.value-val {
  color: #6c757d;
}

.old-value {
  color: #dc3545;
}

.new-value {
  color: #28a745;
}

.event-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
}

.event-created {
  background-color: #d4edda;
  color: #155724;
}

.event-updated {
  background-color: #cce5ff;
  color: #004085;
}

.event-deleted {
  background-color: #f8d7da;
  color: #721c24;
}

.event-restored {
  background-color: #fff3cd;
  color: #856404;
}

.audit-meta {
  font-size: 0.85rem;
  color: #6c757d;
}

.audit-meta i {
  margin-right: 0.25rem;
}

.p-datatable-sm .p-datatable-tbody > tr > td {
  padding: 0.5rem;
}

.audit-paginator {
  font-size: 0.9rem;
  color: #495057;
}
</style>
